import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatAngularModule } from '../../shared/modules/mat-angular.module';
import { Subscription } from 'rxjs';
import { BrandIndexService, Plan, Role } from '../../services/brand-index.service';
import { EducationConfigService, EducationConfigResponse } from '../../services/education-config.service';
import { SelectedUniversity } from '../../shared/components/dynamic-university-search/dynamic-university-search.component';
import { CommonFilterComponent, FilterConfig, FilterData } from '../../shared/components/template-ai-common/common-filter/common-filter.component';
import { CommonTabsComponent, TabsConfiguration, TabData } from '../../shared/components/template-ai-common/common-tabs/common-tabs.component';
import { TemplateCommonService } from '../../shared/components/template-ai-common/services/template-common.service';

@Component({
  selector: 'app-template-ai-education',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAngularModule,
    CommonFilterComponent,
    CommonTabsComponent
  ],
  templateUrl: './template-ai-education.component.html',
  styleUrl: './template-ai-education.component.scss'
})
export class TemplateAiEducationComponent implements OnInit, OnDestroy {
  role: Role = 'student';
  plan: Plan = 'free';

  // Filter configuration for education
  filterConfig: FilterConfig = {
    entityType: 'university',
    entityLabel: 'University',
    entityPlural: 'universities',
    categoryLabel: 'Course',
    categoryOptions: [],
    countryLabel: 'Country',
    countryOptions: [],
    searchPlaceholder: 'Search universities...',
    buttonText: 'Analyze Universities'
  };

  // Tabs configuration for education (from education-v4)
  tabsConfig: TabsConfiguration = {
    entityType: 'university',
    entityLabel: 'University',
    entityPlural: 'universities',
    defaultTab: 'overview',
    tabs: [
      { id: 'overview', name: 'Overview', icon: 'dashboard', component: 'overview', reloadable: true },
      { id: 'ai-visibility-index', name: 'AI Visibility Index', icon: 'visibility', component: 'ai-visibility', enhanced: true, reloadable: true },
      { id: 'ai-citations', name: 'AI Citations', icon: 'format_list_numbered', component: 'ai-citations', enhanced: true, reloadable: true },
      { id: 'category-analysis', name: 'Category Analysis', icon: 'category', component: 'category', reloadable: true },
      { id: 'consumer-insights', name: 'Consumer Insights', icon: 'psychology', component: 'consumer-insights', reloadable: true },
      { id: 'sentiment-tracking', name: 'Sentiment Tracking', icon: 'sentiment_satisfied', component: 'sentiment', enhanced: true, reloadable: true },
      { id: 'area-comparison', name: 'Course Comparison', icon: 'compare_arrows', component: 'area-comparison', visualizationTypes: ['radar', 'bar', 'cards'], enhanced: true, reloadable: true },
      { id: 'ai-content-index', name: 'AI Content Index', icon: 'article', component: 'ai-content-index', enhanced: true, reloadable: true },
      { id: 'content-creation', name: 'Content Creation', icon: 'create', component: 'content-creation', reloadable: true },
      { id: 'performance-tracking', name: 'Performance Tracking', icon: 'trending_up', component: 'performance-tracking', reloadable: true },
      { id: 'methodology', name: 'Our Methodology', icon: 'science', component: 'methodology', reloadable: true },
      // { id: 'student-info', name: 'Student Information', icon: 'info', component: 'student-info', showForRole: 'student' },
      // { id: 'student-comparison', name: 'University Comparison', icon: 'compare_arrows', component: 'student-comparison', visualizationTypes: ['bar', 'cards'], showForRole: 'student', reloadable: true }
    ],
    tableColumns: [
      { key: 'university', label: 'University', type: 'text' },
      { key: 'composite_score', label: 'Composite Score', type: 'number' },
      { key: 'sentiment', label: 'Sentiment', type: 'number' },
      { key: 'tuition_fees', label: 'Tuition Fees', type: 'currency' },
      { key: 'employability', label: 'Employability', type: 'percentage' },
      { key: 'living_cost', label: 'Living Cost', type: 'text' },
      { key: 'visa_psw', label: 'Visa/PSW', type: 'text' }
    ],
    displayedColumns: ['university', 'composite_score', 'sentiment', 'tuition_fees', 'employability', 'living_cost', 'visa_psw'],
    chartMetrics: ['composite_score', 'sentiment', 'employability', 'tuition_fees'],
    maxEntities: 5
  };

  // Results
  table: any[] = [];
  chart: any | null = null;

  // UI
  isLoading = false;
  error: string | null = null;

  // Task-based processing
  hasSearched: boolean = false;

  // Selected entities for tabs
  selectedEntities: SelectedUniversity[] = [];
  category: string = 'MBA';
  country: string = 'UK';

  // Route-based preselection
  preselectedUniversity: string | null = null;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  constructor(
    private api: BrandIndexService,
    private templateCommonService: TemplateCommonService,
    private educationConfigService: EducationConfigService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    // Check for university query parameter
    this.route.queryParams.subscribe(params => {
      if (params['university']) {
        this.preselectedUniversity = decodeURIComponent(params['university']);
        // console.log('Preselected university from route:', this.preselectedUniversity);
      }
    });

    // Initialize data
    this.loadInitialData();

    // Subscribe to service observables
    this.subscribeToServiceObservables();
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private subscribeToServiceObservables(): void {
    // Subscribe to analysis response
    const analysisSub = this.templateCommonService.analysisResponse$.subscribe(response => {
      if (response) {
        // console.log('Template AI Education - Received analysis response:', response);
        this.table = response.table || [];
        this.chart = response.chart || null;
        // console.log('Template AI Education - Updated table and chart data');
      }
    });

    // Subscribe to loading state
    const loadingSub = this.templateCommonService.isLoading$.subscribe(loading => {
      this.isLoading = loading;
    });

    // Subscribe to error state
    const errorSub = this.templateCommonService.error$.subscribe(error => {
      this.error = error;
    });

    // Add subscriptions to array
    this.subscriptions.push(analysisSub, loadingSub, errorSub);
  }

  private loadInitialData(): void {
    // // console.log("loadInitialData");

    // Load config using shared service
    // // console.log("Loading education config from shared service...");

    const configSub = this.educationConfigService.getEducationConfig().subscribe({
      next: (cfg: EducationConfigResponse) => {
        // // console.log("cfg", cfg);
        this.filterConfig.categoryOptions = cfg.courses || [];
        this.filterConfig.countryOptions = (cfg.countries || []);
        if (!this.filterConfig.countryOptions.includes('Global')) {
          this.filterConfig.countryOptions = ['Global', ...this.filterConfig.countryOptions];
        }
        // // console.log("Filter config updated:", this.filterConfig);
      },
      error: (err: any) => {
        // // console.log("error", err);
        console.error("API Error Details:", {
          status: err?.status,
          statusText: err?.statusText,
          message: err?.message,
          url: err?.url,
          error: err?.error
        });
        this.error = `Failed to load config: ${err?.status} ${err?.statusText}`;
      }
    });

    // Add to subscriptions for cleanup
    this.subscriptions.push(configSub);
  }



  onAnalyzeClick(filterData: FilterData): void {
    // console.log('Template AI Education - Analyze clicked:', filterData);
    // Update filter data in service and trigger analysis
    this.templateCommonService.updateFilterData(filterData);
    this.templateCommonService.triggerAnalysis(filterData);
    // console.log('Template AI Education - Analysis triggered for overview tab');
  }

  // Tab event handlers
  onTabChange(tabId: string): void {
    // // console.log('Tab changed to:', tabId);
    // Handle tab change - could load specific tab data here
  }

  onReloadTab(tabId: string): void {
    // console.log('Reloading tab:', tabId);

    // For overview tab, trigger a new analysis through template common service
    if (tabId === 'overview' && this.selectedEntities.length > 0) {
      // Create filter data from current state
      const filterData: FilterData = {
        selectedEntities: this.selectedEntities,
        category: this.category,
        country: this.country
      };

      // console.log('Reloading overview tab with filter data:', filterData);
      this.templateCommonService.updateFilterData(filterData);
      this.templateCommonService.triggerAnalysis(filterData);
    }
  }

  onVisualizationChange(event: { tabId: string, type: string }): void {
    // // console.log('Visualization changed:', event);
    // Handle visualization change
  }

  // Update selected entities when filter changes
  onFilterChange(filterData: FilterData): void {
    // console.log('Filter changed:', filterData);
    // Update selected entities and category/country for tabs
    this.selectedEntities = filterData.selectedEntities;
    this.category = filterData.category;
    this.country = filterData.country;

    // Update the template common service with the new filter data
    this.templateCommonService.handleFilterChange(filterData);

    // Mark that user has performed a search when entities are selected
    if (filterData.selectedEntities.length > 0) {
      this.hasSearched = true;
    }
  }
}
